import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import WhatsAppLogo from "@/components/site/WhatsAppLogo";
import { useI18n } from "@/i18n/I18nProvider";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Home Hero — light minimal theme.
 * Structure:
 *  - LEFT column: eyebrow, big brand headline, subheading, CTAs, stats
 *  - RIGHT column: featured product image with subtle parallax and floating label
 */
export default function Hero() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => { axios.get(`${API}/products?featured=true`).then((r) => setFeatured(r.data || [])).catch(() => {}); }, []);

  const heroImg = featured[0]?.image;
  const heroImgAlt = featured[0]?.title || "Ramdev Steel product";

  return (
    <section
      ref={ref}
      data-testid={HOME.hero}
      className="relative w-full overflow-hidden pt-20 md:pt-24 bg-white"
    >
      {/* Soft steel-blue background wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[var(--steel-soft)] opacity-70 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-[var(--steel-soft)] opacity-60 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pt-8 md:pt-14 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

          {/* LEFT: copy */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 mb-6 md:mb-8">
              <span className="w-8 h-px bg-[var(--steel)]" />
              <span className="label-eyebrow">{t.hero.eyebrow}</span>
            </motion.div>

            <h1 className="font-display font-medium text-slate-900 leading-[1.02] tracking-tight text-[11vw] sm:text-6xl md:text-7xl lg:text-[5.2rem] xl:text-[6rem]">
              <span className="block reveal-mask">
                <motion.span
                  className="reveal-line"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, ease: [0.2, 0.7, 0.2, 1], delay: 0.15 }}
                >
                  {t.hero.brand}
                </motion.span>
              </span>
              <span className="block reveal-mask">
                <motion.span
                  className="reveal-line text-[var(--steel)]"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, ease: [0.2, 0.7, 0.2, 1], delay: 0.32 }}
                >
                  {t.hero.brand2}
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="mt-8 md:mt-10 text-slate-600 max-w-xl text-base md:text-lg leading-relaxed"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="mt-8 md:mt-10 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/products"
                data-testid={HOME.ctaView}
                className="btn-steel group inline-flex items-center gap-2 px-6 py-3.5 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full"
              >
                {t.hero.view}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href="https://wa.me/919731852956"
                target="_blank"
                rel="noreferrer"
                data-testid={HOME.ctaWA}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#25D366] hover:bg-[#1EBE57] text-white text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full transition-colors shadow-sm"
              >
                <WhatsAppLogo size={16} /> {t.hero.whatsapp}
              </a>
              <Link
                to="/contact"
                data-testid={HOME.ctaQuote}
                className="btn-outline inline-flex items-center gap-2 px-6 py-3.5 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full"
              >
                {t.hero.quote}
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-10 md:mt-14 grid grid-cols-3 gap-4 max-w-md">
              <StatMini k="18+" v="Years" />
              <StatMini k="1200+" v="Installs" />
              <StatMini k="8" v="Collections" />
            </motion.div>
          </div>

          {/* RIGHT: hero image (parallax) */}
          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: y1 }}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1], delay: 0.35 }}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--bg-1)] shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]"
            >
              {heroImg && (
                <img
                  src={heroImg}
                  alt={heroImgAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />

              {/* Floating card */}
              <motion.div
                style={{ y: y2 }}
                className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-4 md:p-5 shadow-md"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="label-eyebrow text-[var(--steel)]">Featured</div>
                    <div className="mt-1 font-display text-slate-900 text-lg md:text-xl truncate">{featured[0]?.title || "Ramdev Steel"}</div>
                  </div>
                  <Link
                    to="/products"
                    className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-[var(--steel)] text-white hover:bg-[var(--steel-hover)] transition-colors"
                    aria-label="View products"
                  >
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatMini({ k, v }) {
  return (
    <div>
      <div className="font-display font-semibold text-slate-900 text-3xl md:text-4xl leading-none tracking-tight">{k}</div>
      <div className="label-eyebrow mt-2">{v}</div>
    </div>
  );
}
